/// <amd-module name="Build/Common/Library/PipelineResourcesService" />
import { DefinitionResourceReference } from "Build/Client/Contracts/Build";
import { IBuildRestClient } from "Build/Client/RestClient/Build";
import * as Resources from "Build/Common/Library/Resources";

import { ITfsPageService } from "Tfs/Platform/Page";

import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IServerException, IVssContributionService } from "VSS/Platform/Contribution";

export interface IPipelineResourcesService extends IVssService {
    /**
     * Authorize/Unauthorize the specified resource for the pipeline.
     */
    updateAuthorizedResource(pipelineId: number, resourceType: string, resourceId: string, authorized: boolean): void;

    /**
     * Attempt to automatically authorize resources referenced in the pipeline's YAML file.
     */
    autoAuthorizeResources(pipelineId: number, sourceBranch: string): Promise<DefinitionResourceReference[]>;
}

const resourceAuthorizationDataProviderId = "ms.vss-build-web.authorize-pipeline-resources-data-provider";

class PipelineResourcesService extends VssService implements IPipelineResourcesService {
    /**
     * Authorize/Unauthorize the specified resource for the pipeline.
     */
    public updateAuthorizedResource(pipelineId: number, resourceType: string, resourceId: string, authorized: boolean): Promise<DefinitionResourceReference[]> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        let projectId = "";
        if (tfsPageData && tfsPageData.project) {
            projectId = tfsPageData.project.id;
        }

        const resources: DefinitionResourceReference[] = [
            {
                authorized: authorized,
                id: resourceId,
                type: resourceType,
                name: "" // name isn't necessary for updates
            }
        ];

        const buildClient = this.pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
        return buildClient.authorizeDefinitionResources(resources, projectId, pipelineId);
    }

    /**
     * Attempt to automatically authorize resources referenced in the pipeline's YAML file.
     */
    public autoAuthorizeResources(pipelineId: number, sourceBranch: string): Promise<DefinitionResourceReference[]> {
        const requestParams = {
            pipelineId,
            sourceBranch
        };

        return new Promise((resolve, reject) => {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            contributionService.getDataAsync<DefinitionResourceReference[]>(resourceAuthorizationDataProviderId, requestParams, true)
                .then((result: DefinitionResourceReference[]) => {
                    if (result) {
                        resolve(result);
                    }
                    else {
                        const ex: IServerException = contributionService.getDataProviderExceptions()[resourceAuthorizationDataProviderId];
                        const message = (ex && ex.message) || Resources.AutoAuthorizeErrorMessage;
                        reject(new Error(message));
                    }
                });
        });
    }
}

Services.add("IPipelineResourcesService", { serviceFactory: PipelineResourcesService });